import { Search } from "lucide-react";
import React, { useContext, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Avatar,
  Group,
  LoadingOverlay,
  Space,
  Text,
  TextInput,
} from "@mantine/core";
import TagAssignee from "../services/model/TagAssignee";
import { AuthContext } from "../../../providers/AuthProvider";
import useEmployeeListApiService from "../../employees/services/api/EmployeeListApiService";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import EmployeeTableEmptyStateContainer from "../../employees/components/EmployeeTableEmptyStateContainer";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import Pagination from "../../../themes/components/Pagination";
import TagAddSelectedEmployeesButton from "../elements/TagAddEmployeeButton";

type TagAssigneeFormProps = {
  id: number;
  assignee: TagAssignee[];
  onSuccess: () => void;
};

const TagAssigneeForm: React.FC<TagAssigneeFormProps> = ({
  assignee,
  id,
  onSuccess,
}) => {
  const [selectedEmployees, setSelectedEmployees] =
    useState<TagAssignee[]>(assignee);
  const [search, setSearch] = useState("");
  const [activePage, setActivePage] = useState(1);

  const auth = useContext(AuthContext);
  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  const { isLoading, isError, error, data, isFetching } =
    useEmployeeListApiService({
      employer: auth.employer.id,
      pagination: true,
      page: activePage,
      tsAndCsAccepted: false,
      search,
    });

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!data) {
    return <EmployeeTableEmptyStateContainer />;
  }

  if (data && !data.results.length && !search.length) {
    return <EmployeeTableEmptyStateContainer />;
  }

  return (
    <>
      <LoadingOverlay visible={isFetching} />
      <Space h="sm" />
      <TextInput
        placeholder="Search"
        onChange={(e) => setSearch(e.target.value.toLowerCase())}
        value={search}
        rightSection={<Search strokeWidth={1} />}
      />

      <Space h="md" />

      <DirectoryTable
        data={data.results.map((it) => ({
          id: it.id,
          first_name: it.first_name,
          last_name: it.last_name,
          date_created: it.date_created,
          email: it.email,
          status: it.smartwage_status,
        }))}
        selection={selectedEmployees}
        onSelect={setSelectedEmployees}
        headers={["Full name"]}
        rows={data.results.map((it) => ({
          name: (
            <Group spacing="sm">
              <Avatar size={26} radius={26} />
              <Text size="sm">{`${it.first_name} ${it.last_name}`}</Text>
            </Group>
          ),
        }))}
      />

      {data && data.pages > 1 && (
        <Pagination
          onChange={setActivePage}
          total={data.pages}
          initialPage={1}
        />
      )}

      <TagAddSelectedEmployeesButton
        assignee={assignee}
        selectedEmployees={selectedEmployees}
        id={id}
        onSuccess={onSuccess}
      />
    </>
  );
};

export default TagAssigneeForm;
